// File: Article10IncredibleVancouverHikes.jsx

import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function Article10IncredibleVancouverHikes() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "10-incredible-vancouver-hikes";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles
    ? articles.find((art) => art.slug === ref_id)
    : null;

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Lighthouse Park (Easy)",
    "section-2": "Dog Mountain (Easy)",
    "section-3": "Bowen Lookout (Easy)",
    "section-4": "Eagle Bluffs (Moderate)",
    "section-5": "St. Mark’s Summit (Moderate)",
    "section-6": "Mount Seymour Trail (Moderate)",
    "section-7": "Stawamus Chief (Challenging)",
    "section-8": "Diez Vistas (Challenging)",
    "section-9": "Elk Mountain (Challenging)",
    "section-10": "Garibaldi Lake (Challenging)",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://bcparks.ca",
      label: "BC Parks – Official Website",
    },
    {
      url: "https://vancouvertrails.com",
      label: "Vancouver Trails – Detailed Trail Guides",
    },
    {
      text: "North Shore Rescue: Hiking Safety Essentials and Trip Planning Guides",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's ID corresponds to the matching section or paragraph.
  // Coordinates must match EXACTLY the data used in the conversation.
  // We'll use official or approximate lat/lng to 5 decimals.
  const locations = [
    {
      id: "section-1", // Lighthouse Park
      lat: 49.33745,
      lng: -123.26299,
      popupText: "Lighthouse Park, West Vancouver (Easy)",
    },
    {
      id: "section-2", // Dog Mountain
      lat: 49.36758,
      lng: -122.94911,
      popupText: "Dog Mountain Trail (Easy)",
    },
    {
      id: "section-3", // Bowen Lookout
      lat: 49.39586,
      lng: -123.20389,
      popupText: "Bowen Lookout at Cypress (Easy)",
    },
    {
      id: "section-4", // Eagle Bluffs
      lat: 49.39586,
      lng: -123.20389,
      popupText: "Eagle Bluffs (Moderate) – same trailhead at Cypress",
    },
    {
      id: "section-5", // St. Mark's Summit
      lat: 49.42819,
      lng: -123.20616,
      popupText: "St. Mark’s Summit (Moderate)",
    },
    {
      id: "section-6", // Mount Seymour (First Peak)
      lat: 49.39333,
      lng: -122.94444,
      popupText: "Mount Seymour Trail (Moderate)",
    },
    {
      id: "section-7", // Stawamus Chief
      lat: 49.68639,
      lng: -123.13694,
      popupText: "Stawamus Chief (Challenging)",
    },
    {
      id: "section-8", // Diez Vistas
      lat: 49.33761,
      lng: -122.85650,
      popupText: "Diez Vistas (Challenging) – Buntzen Lake area",
    },
    {
      id: "section-9", // Elk Mountain
      lat: 49.10493,
      lng: -121.82068,
      popupText: "Elk Mountain in Chilliwack (Challenging)",
    },
    {
      id: "section-10", // Garibaldi Lake
      lat: 49.95739,
      lng: -123.11969,
      popupText: "Garibaldi Lake (Challenging) – Rubble Creek trailhead",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // Mobile map reference
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>10 Incredible Vancouver Hikes You Can’t Miss!</title>
        <meta
          name="description"
          content="Discover the top 10 Vancouver hikes—from easy forest walks to challenging mountain ascents. Includes detailed trail info, tips, FAQs, and local regulations."
        />
        <meta
          name="keywords"
          content="Vancouver hikes, best hiking trails, Lighthouse Park, Dog Mountain, Bowen Lookout, Eagle Bluffs, St. Mark’s Summit, Mount Seymour, Stawamus Chief, Diez Vistas, Elk Mountain, Garibaldi Lake"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/10-incredible-vancouver-hikes.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="10 Incredible Vancouver Hikes You Can’t Miss!"
        />
        <meta
          property="og:description"
          content="From easy coastal trails to alpine adventures—your ultimate Vancouver hiking guide."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/10-incredible-vancouver-hikes.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/10-incredible-vancouver-hikes.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="10 Incredible Vancouver Hikes You Can’t Miss!"
        />
        <meta
          name="twitter:description"
          content="Explore breathtaking trails in and around Vancouver with this comprehensive hiking guide."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/10-incredible-vancouver-hikes.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TOC + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Stunning Vancouver mountain scenery"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a hiker’s paradise, nestled between the Pacific Ocean
              and the Coast Mountains. This unique geography means you can find
              everything from serene forest walks to challenging mountain
              ascents within a short drive of the city. Whether you’re a tourist
              looking to experience British Columbia’s famed wilderness or a
              local exploring a new trail, Vancouver’s diverse hiking options
              offer spectacular scenery at every turn—from city skyline and
              ocean vistas to alpine lakes and old-growth forests.
            </p>
            <p>
              In this guide, we’ve curated{" "}
              <strong>10 incredible hikes around Vancouver</strong> that you
              can’t miss. The list balances easy, moderate, and challenging
              trails suitable for all experience levels. Each recommendation
              includes practical details like distance, duration, and
              coordinates, plus tips on the best time to go and what highlights
              to look for. Get ready to explore why this region is
              world-renowned for outdoor adventure!
            </p>

            {/* SECTION 1: Lighthouse Park (Easy) */}
            <h2 id="section-1">Lighthouse Park (Easy)</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.33745}
                coordX={-123.26299}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> Variable loops up to ~10 km;
              a popular route is ~3.3 km taking about 1.5–2 hours. <br />
              <strong>Highlights:</strong> Explore a coastal old-growth
              rainforest with giant Douglas firs and cedars. Short trails lead
              to rocky shorelines with fantastic views of downtown Vancouver,
              Stanley Park, and the Salish Sea. The historic Point Atkinson
              Lighthouse, built in 1912, is perched on scenic granite bluffs.
              Bald eagles often soar overhead, and you might spot seals offshore.
              <br />
              <strong>Trailhead & Parking:</strong> Located in West Vancouver at
              the end of Beacon Lane. Parking is free but limited, so arrive
              early. The park is open daily from dawn to dusk. <br />
              <strong>Safety Tips:</strong> Trails are well-marked and mostly
              easy, but watch for slippery rocks near the shoreline. Dogs must
              be leashed, and you should stay on established paths to avoid
              disorientation among the various intersecting trails.
            </p>

            {/* SECTION 2: Dog Mountain (Easy) */}
            <h2 id="section-2">Dog Mountain (Easy)</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.36758}
                coordX={-122.94911}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> 5 km round trip; ~2 hours
              out-and-back. <br />
              <strong>Highlights:</strong> A short hike with a big payoff—Dog
              Mountain’s bluff offers panoramic views over Vancouver, Burrard
              Inlet, and Mount Baker on clear days. The trail passes through
              subalpine forest, including First Lake. A great option for
              families or beginners looking for a quick escape close to the
              city. <br />
              <strong>Trailhead & Access:</strong> Start from the top parking
              lot of Mount Seymour Provincial Park in North Vancouver. No fees
              or permits required. The trail can be muddy and rooty; wear sturdy
              footwear. In winter, this is also a popular snowshoe route. <br />
              <strong>Safety Tips:</strong> Watch out for slippery boardwalks
              and keep an eye on the trail markers to avoid straying off-route.
              Bring microspikes if you plan to go in snowy conditions.
            </p>

            {/* SECTION 3: Bowen Lookout (Easy) */}
            <h2 id="section-3">Bowen Lookout (Easy)</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.39586}
                coordX={-123.20389}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> 4 km round trip; ~1.5 hours.
              <br />
              <strong>Highlights:</strong> This short but scenic trail leads to
              a bluff overlooking Howe Sound, with Bowen Island directly below.
              The vantage point is stunning—on clear days, you’ll see the
              Sunshine Coast and Vancouver Island in the distance. <br />
              <strong>Trailhead & Parking:</strong> Cypress Mountain Ski Area in
              West Vancouver. The initial part is a boardwalk around Yew Lake,
              then it becomes steeper and rooty. No permit needed, but parking
              can be busy on weekends. <br />
              <strong>Safety Tips:</strong> The lookout has steep drop-offs,
              so keep back from the edges. Dogs are not allowed on the Yew Lake
              Trail/Bowen Lookout route per park rules. Bring a flashlight if
              aiming for sunset.
            </p>

            {/* SECTION 4: Eagle Bluffs (Moderate) */}
            <h2 id="section-4">Eagle Bluffs (Moderate)</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates (Trailhead shared with Bowen Lookout):</strong>{" "}
              <Marker
                coordY={49.39586}
                coordX={-123.20389}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> ~8 km round trip; 4 hours.{" "}
              <br />
              <strong>Highlights:</strong> A North Shore favorite with
              jaw-dropping views from the bluff. You’ll see Vancouver, Howe
              Sound, and Bowen Island from a high vantage. The hike ascends
              Black Mountain first, then drops down to Eagle Bluffs. <br />
              <strong>Trailhead & Route:</strong> Also starts at Cypress
              Mountain in West Vancouver. The terrain can be steep and muddy.
              No permit required. <br />
              <strong>Safety Tips:</strong> Watch for the sheer drops at the
              viewpoint. Dogs on leash are allowed, unlike Bowen Lookout.
              Carry enough water, especially in summer.
            </p>

            {/* SECTION 5: St. Mark’s Summit (Moderate) */}
            <h2 id="section-5">St. Mark’s Summit (Moderate)</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates (Summit Viewpoint):</strong>{" "}
              <Marker
                coordY={49.42819}
                coordX={-123.20616}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> 11 km round trip; ~5 hours.
              <br />
              <strong>Highlights:</strong> Part of the Howe Sound Crest Trail,
              St. Mark’s Summit offers panoramic coastal views of Howe Sound,
              Gambier Island, and beyond. It’s one of the North Shore’s most
              popular day hikes. <br />
              <strong>Trailhead & Logistics:</strong> Same Cypress parking lot
              as Eagle Bluffs. The path is well-traveled but can have some
              confusing junctions. No permit needed. <br />
              <strong>Safety Tips:</strong> Stick to the marked route. The cliff
              edges at the top have no railings. Weather can change quickly;
              bring layers.
            </p>

            {/* SECTION 6: Mount Seymour Trail (Moderate) */}
            <h2 id="section-6">Mount Seymour Trail (Moderate)</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates (First/Second Peak Area):</strong>{" "}
              <Marker
                coordY={49.39333}
                coordX={-122.94444}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> ~9 km round trip; 4–5 hours.
              <br />
              <strong>Highlights:</strong> Three peaks, each offering superb
              views of Vancouver, the Coast Mountains, and even Mount Baker on
              clear days. The terrain transitions from thick forest to alpine
              meadows and rocky summits. <br />
              <strong>Trailhead & Getting There:</strong> Mount Seymour Road in
              North Vancouver. No permit or fee for day hiking. <br />
              <strong>Safety Tips:</strong> The trail can be rocky and steep at
              points. Navigation can be challenging in snow; follow markers.
              Always bring the 10 essentials.
            </p>

            {/* SECTION 7: Stawamus Chief (Challenging) */}
            <h2 id="section-7">Stawamus Chief (Challenging)</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.68639}
                coordX={-123.13694}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> ~11 km round trip for all 3
              peaks; 6 hours. <br />
              <strong>Highlights:</strong> The iconic granite monolith in
              Squamish. Each of the three peaks offers different perspectives of
              Howe Sound, Squamish Valley, and surrounding mountains. Steep
              sections with ladders and chains add a thrill. <br />
              <strong>Trailhead & Route:</strong> Stawamus Chief Provincial Park
              parking lot, just south of Squamish. The climb starts with many
              wooden stairs. <br />
              <strong>Safety Tips:</strong> Steep ladders, chains, and
              cliff-edge scrambles. Keep children and dogs under control. Bring
              plenty of water and be prepared for crowds.
            </p>

            {/* SECTION 8: Diez Vistas (Challenging) */}
            <h2 id="section-8">Diez Vistas (Challenging)</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates (Buntzen Lake Trailhead):</strong>{" "}
              <Marker
                coordY={49.33761}
                coordX={-122.8565}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> 15 km loop; 6–7 hours. <br />
              <strong>Highlights:</strong> “Ten views” of Indian Arm, Burrard
              Inlet, and Buntzen Lake. The trail ascends a ridge with multiple
              vantage points, then returns via lakeside paths. <br />
              <strong>Trailhead & Access:</strong> Buntzen Lake Recreational
              Area near Port Moody. In summer, a free parking pass is required
              (reserve online). <br />
              <strong>Safety Tips:</strong> Parts of the trail are rugged with
              exposed roots and rocks. Bear sightings are possible. Navigation
              can be tricky; carry a map or GPS track.
            </p>

            {/* SECTION 9: Elk Mountain (Challenging) */}
            <h2 id="section-9">Elk Mountain (Challenging)</h2>
            <p
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates (Trailhead):</strong>{" "}
              <Marker
                coordY={49.10493}
                coordX={-121.82068}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> ~8 km round trip; 4 hours.{" "}
              <br />
              <strong>Highlights:</strong> Located in Chilliwack, Elk Mountain
              provides sweeping views of the Fraser Valley, Cultus Lake, and the
              North Cascades. In summer, wildflower meadows dot the ridge. <br />
              <strong>Trailhead & Access:</strong> Elk View Road; about 1.5
              hours’ drive from Vancouver. No permit required. No facilities on
              site. <br />
              <strong>Safety Tips:</strong> The trail is short but steep. Trekking
              poles help on the ascent and descent. Pack enough water. Bear
              encounters are possible; carry bear spray if you’re comfortable
              using it.
            </p>

            {/* SECTION 10: Garibaldi Lake (Challenging) */}
            <h2 id="section-10">Garibaldi Lake (Challenging)</h2>
            <p
              ref={(el) => setSpotRef("section-10", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates (Rubble Creek Trailhead):</strong>{" "}
              <Marker
                coordY={49.95739}
                coordX={-123.11969}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <strong>Distance & Duration:</strong> 18 km round trip; ~7 hours.
              <br />
              <strong>Highlights:</strong> Often cited as one of BC’s most
              scenic hikes. At 1,484 m elevation, Garibaldi Lake’s turquoise
              waters are surrounded by snow-capped peaks and glaciers. <br />
              <strong>Trailhead & Permits:</strong> Rubble Creek, ~30 minutes
              south of Whistler. A free Day-Use Permit is required in summer
              (book online via BC Parks). <br />
              <strong>Safety Tips:</strong> Pace yourself on the long switchbacks.
              Bring bug spray for summer mosquitoes. Weather can change rapidly
              in the alpine, so pack layers. No dogs allowed in Garibaldi
              Provincial Park.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Vancouver’s trails are heavily used, and responsible hiking is
              vital for safety and conservation. Always carry the “Ten
              Essentials” (navigation, flashlight, first aid, extra food, water,
              extra clothing, sun protection, pocket knife, fire starter, and
              emergency shelter). Plan your trip in advance—check weather
              forecasts and trail conditions. Cellular reception can be spotty
              in the mountains. Leave a trip plan with someone, especially for
              remote or challenging hikes.
            </p>
            <p>
              Follow <strong>Leave No Trace</strong> principles: stay on marked
              trails, pack out all litter (including organic waste), don’t feed
              wildlife, and keep noise levels down. Some parks require day-use
              passes or parking reservations (e.g., Garibaldi Provincial Park,
              Buntzen Lake). Dogs may be restricted or require leashes
              depending on the park. Avoid open fires outside designated areas,
              especially in summer when fire bans are common.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a permit for these hikes?</h3>
              <p>
                Most Vancouver-area hikes are free, but a few popular trails
                require a Day-Use Pass in peak season (like Garibaldi Lake).
                Some trailheads (e.g., Buntzen Lake) need a free parking
                reservation. Always check official park websites for
                up-to-date info on permits and fees.
              </p>

              <h3>2. Are these hikes accessible by public transit?</h3>
              <p>
                A few, such as Lighthouse Park (West Vancouver bus) and the base
                of Grouse Mountain (not listed here, but popular), are somewhat
                reachable by public transit. However, most trailheads (Mount
                Seymour, Cypress, Elk Mountain, Garibaldi) require a car or
                rideshare/taxi. Planning transportation in advance is highly
                recommended.
              </p>

              <h3>3. Is wildlife a concern?</h3>
              <p>
                These trails are in black bear and cougar territory. Incidents
                are rare, but you should make noise, travel in groups, and never
                feed or approach wildlife. Bear spray is optional but can be
                carried if you know how to use it. Keep dogs on leash to avoid
                surprising wildlife.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s hiking trails offer an incredible variety of
              experiences—from the sea-level forests of Lighthouse Park to the
              lofty vistas of Garibaldi Lake. These 10 hikes showcase why the
              region is celebrated for outdoor adventure. Prepare well, follow
              local regulations, and treat these natural areas with respect.
              Lace up your boots, breathe the fresh mountain air, and discover
              the unforgettable views waiting on Vancouver’s best trails.
            </p>

            {/* BOTTOM COMPONENT: references, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}